import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Users, Award, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const About = () => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const stats = [
    { icon: Clock, value: settings?.about_stats_annees || "15+", label: "Années d'expérience" },
    { icon: Users, value: settings?.about_stats_clients || "5000+", label: "Clients satisfaits" },
    { icon: Award, value: settings?.about_stats_vehicules || "500+", label: "Véhicules vendus" },
  ];

  const values = [
    "Expertise technique certifiée",
    "Transparence totale sur les prix",
    "Garantie sur tous nos véhicules",
    "Service personnalisé",
    "Pièces de qualité d'origine",
    "Respect des délais"
  ];

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative py-32" data-testid="about-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1690674569400-0396f33445b5?w=1920')`
          }}
        />
        <div className="absolute inset-0 bg-black/60" />
        
        <div className="relative page-container">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
            {settings?.about_titre?.split(" ").slice(0, 2).join(" ") || "À PROPOS"} <span className="text-yellow-400">{settings?.about_titre?.split(" ").slice(2).join(" ") || "D'ACICAR"}</span>
          </h1>
          <p className="text-xl text-gray-200 max-w-2xl">
            {settings?.about_description || "Depuis plus de 15 ans, nous mettons notre expertise au service de votre automobile à Crépy-en-Valois."}
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="page-container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index}
                className="text-center p-8 bg-gray-50 rounded-xl border border-gray-200"
              >
                <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-8 h-8 text-yellow-600" />
                </div>
                <div className="text-4xl font-bold text-yellow-500 mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="section-spacing bg-gray-50">
        <div className="page-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                NOTRE <span className="text-yellow-500">HISTOIRE</span>
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  {settings?.about_histoire || "Fondé en 2009, ACICAR est né de la passion d'une équipe de mécaniciens expérimentés souhaitant offrir un service automobile de qualité à Crépy-en-Valois et ses environs."}
                </p>
                <p>
                  {settings?.about_mission || "Notre mission est simple : vous accompagner dans tous vos projets automobiles, que ce soit pour l'achat d'un véhicule d'occasion ou pour l'entretien et la réparation de votre véhicule actuel."}
                </p>
                <p>
                  Aujourd'hui, nous sommes fiers d'avoir accompagné plus de {settings?.about_stats_clients || "5000"} clients 
                  et vendu plus de {settings?.about_stats_vehicules || "500"} véhicules, toujours avec le même engagement : 
                  la satisfaction client avant tout.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1717068341263-33331ec8104c?w=800"
                alt="Atelier ACICAR"
                className="rounded-xl w-full shadow-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-yellow-500 text-white p-6 rounded-xl shadow-lg">
                <div className="text-3xl font-bold">{settings?.about_stats_annees || "15+"}</div>
                <div className="text-sm">Années d'expertise</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-spacing bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              NOS <span className="text-yellow-500">ENGAGEMENTS</span>
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Des valeurs qui guident notre travail au quotidien
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 p-6 bg-gray-50 rounded-xl border border-gray-200"
              >
                <CheckCircle className="w-6 h-6 text-yellow-500 flex-shrink-0" />
                <span className="text-gray-800">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-yellow-500">
        <div className="page-container text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            VENEZ NOUS RENCONTRER
          </h2>
          <p className="text-white/80 max-w-2xl mx-auto mb-8">
            Notre équipe vous accueille du lundi au samedi pour discuter 
            de vos projets automobiles.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button className="bg-white text-yellow-600 hover:bg-gray-100 font-bold uppercase">
                Nous contacter
              </Button>
            </Link>
            <Link to="/voitures">
              <Button variant="outline" className="border-white text-white hover:bg-white/20">
                Voir nos véhicules
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
